import { AccessLevel } from '@/modules/common/enums/access-levels.enum'
import GtrSuper from '@/modules/common/components/mixins/gtr-super.mixin'
import GtrEventBookmarks from '@/modules/common/components/ui-core/gtr-event-bookmarks/gtr-event-bookmarks.vue'
import GtrChangeLog from '@/modules/common/components/ui-core/gtr-change-log/gtr-change-log.vue'
import FloatingPrintButton from '@/modules/common/components/ui-core/floating-print-button/floating-print-button.vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { mapGetters, mapState } from 'vuex'
import GtrModuleMenuItem from '@/modules/common/components/ui-core/gtr-module-menu-item/gtr-module-menu-item.vue'
import { Container } from 'typedi'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import { canReadBadgeSettings } from '@/modules/common/utils/badge-settings-permission.util'

@Component({
  name: 'GtrModuleLayout',
  computed: {
    ...mapState('company', ['company']),
    ...mapState('event', ['event', 'eventAllContent']),
    ...mapState('common', ['latestChangelog', 'version', 'noticeDisplayed']),
    ...mapState('module', ['eventOverview', 'gtrModules', 'activatedEventModules', 'support']),
    ...mapState('navigation', { miniVariant: (state: any) => state.moduleLayoutMiniVariant }),
    ...mapGetters('media', ['defaultFonts', 'fontList'])
  },
  components: {
    'gtr-change-log': GtrChangeLog,
    'gtr-module-menu-item': GtrModuleMenuItem,
    'gtr-event-bookmarks': GtrEventBookmarks,
    'floating-print-button': FloatingPrintButton
  }
})
export default class GtrModuleLayout extends GtrSuper {
  activatedEventModules!: Record<string, any>;
  eventOverview!: any;
  gtrModules!: any;
  support!: any;
  noticeDisplayed!: boolean;
  defaultFonts!: Array<Record<string, any>>;
  fontList!: Array<Record<string, any>>;
  eventAllContent!: any;
  AccessLevel = AccessLevel

  get menuItems () {
    return [
      this.eventOverview,
      ...this.gtrModules,
      this.support
    ]
  }

  get isMobile (): boolean {
    return this.$vuetify.breakpoint.xsOnly
  }

  data () {
    return {
      changeLogModal: false,
      changeLogVersion: null,
      changeLog: null,
      showVersion: false,
      mountComplete: false,
      legacyNavOpen: false
    }
  }

  async mounted () {
    if (!this.$store.state.security.currentUser) {
      await this.$store.dispatch('security/getCurrentUser')
    }
    await this.loadEventAndCompanyInfo()
    await this.fetchEventModules()
    await this.fetchBadgeSettings()
    await this.fetchFontList()
    this.$data.mountComplete = true
  }

  @Watch('latestChangelog', { immediate: true })
  onLatestChangelogChange (payload: any) {
    if (payload) {
      this.$data.changeLog = payload
    }
  }

  @Watch('version', { immediate: true })
  onVersionChange (payload: any) {
    if (payload) {
      this.$data.changeLogVersion = payload
    }
  }

  @Watch('eventAllContent', { immediate: true })
  onEventAllContentChange (payload: any) {
    if (payload?.orders_settings) {
      this.$store.commit('module/SET_GTR_MODULE_SUBMODULE_VISIBILITY', {
        moduleIdentifier: 'LEADS',
        routeName: 'level-two.modules.leads.pricing-tiers',
        active: !!payload.orders_settings.use_legacy_leads_pricing
      })
    }
  }

  setMiniVariant (value: boolean) {
    this.$store.dispatch('navigation/setModuleLayoutMiniVariant', value)
  }

  navigateTo (route: any) {
    this.$router.push(route)
  }

  private async loadEventAndCompanyInfo () {
    this.$store.dispatch('common/showLoader', { value: true })
    try {
      await this.$store.dispatch('company/fetchCompany', this.$route.params.uuid)
      await this.$store.dispatch('event/fetchEvent', this.$route.params.event_uuid)
      await this.$store.dispatch('event/getEventAllContent', this.$route.params.event_uuid)
      this.$bus.$emit('gtr.event.content_all')
    } catch (error) {
    } finally {
      this.$store.dispatch('common/hideLoader')
    }
  }

  private onUpdateMenu (module: any) {
    this.$data.module = module
    let event_uuid: any = null
    if (this.$store.state.event.event) {
      if (this.$store.state.event.event.uuid !== this.$route.params.event_uuid) {
        event_uuid = this.$store.state.event.event.uuid
      } else {
        event_uuid = this.$route.params.event_uuid
      }
    } else {
      event_uuid = this.$route.params.event_uuid
    }
    this.$data.menuItems = this.makeSideBarMenuModule(module, this.$route.params.uuid, event_uuid)
  }

  @Watch('$route', { immediate: true })
  async onRouteChange () {
    if (this.$route.params.uuid && this.$route.params.event_uuid) {
      if (this.$data.module) {
        this.$data.menuItems = this.makeSideBarMenuModule(this.$data.module, this.$route.params.uuid, this.$route.params.event_uuid)
      }
      if (this.$store.state.event.eventAllContent) {
        if (this.$store.state.event.eventAllContent.event.uuid !== this.$route.params.event_uuid) {
          await this.loadEventAndCompanyInfo()
          this.$bus.$emit('gtr-fetch-general-survey')
        }
      }
    }
  }

  private async fetchEventModules () {
    try {
      const { event_uuid } = this.$route.params
      await this.$store.dispatch('module/fetchEventModules', event_uuid)
    } catch (err) {
      Container.get(ErrorHandlerService).error(err)
    }
  }

  private async fetchBadgeSettings () {
    if (!canReadBadgeSettings((key: string) => this.$hasPermission(key))) {
      return
    }
    try {
      const { event_uuid } = this.$route.params
      await this.$store.dispatch('badges/getSettings', { event_uuid })
    } catch (err) {
      Container.get(ErrorHandlerService).error(err)
    }
  }

  async fetchFontList () {
    const { uuid: companyUuid } = this.$route.params
    await this.$store.dispatch('media/getFontStores', companyUuid)
  }
}
